import { useState, useMemo, useCallback, type ReactNode } from "react";
import {
  useTellescopeInfiniteQuery,
  TellescopeModelFromName,
  type UseTellescopeInfiniteQueryOptions,
  type ParamsForMethod,
} from "@tellescope/tanstack-query";
import {
  Combobox,
  ComboboxOption,
  ComboboxOptionRenderProps,
  Avatar,
  AvatarFallback,
  AvatarImage,
  DeletableBadge,
  useDebouncedValue,
} from "@tellescope/ui";
import { formatName, formatInitials, formatPhone, cn } from "@tellescope/utils";
import { Check } from "lucide-react";
import { i18n } from "@tellescope/i18n";

/**
 * Type representing an enduser model from the Tellescope API
 */
export type Enduser = TellescopeModelFromName<"endusers">;

/**
 * Available default option renderer variants
 */
export type DefaultRendererVariant = "default" | "phone" | "email";

/**
 * Props for the default enduser option renderer
 */
interface DefaultEnduserOptionProps {
  enduser: Enduser;
  isSelected: boolean;
  variant: DefaultRendererVariant;
}

/**
 * Default option renderer component with three variants
 */
function DefaultEnduserOption({ enduser, isSelected, variant }: DefaultEnduserOptionProps): ReactNode {
  return (
    <>
      <div className="flex gap-2 items-center w-full">
        <Avatar className="h-8 w-8">
          <AvatarImage src={enduser.avatar} />
          <AvatarFallback>{formatInitials(enduser)}</AvatarFallback>
        </Avatar>
        <div className="flex flex-col min-w-0 flex-1">
          <p className="font-semibold text-sm truncate">{formatName(enduser)}</p>
          {variant === "phone" && enduser.phone && (
            <p className="text-xs text-muted-foreground truncate">{formatPhone(enduser.phone)}</p>
          )}
          {variant === "email" && enduser.email && (
            <p className="text-xs text-muted-foreground truncate">{enduser.email}</p>
          )}
        </div>
      </div>
      <Check className={cn("ml-2 h-4 w-4 shrink-0", isSelected ? "opacity-100" : "opacity-0")} />
    </>
  );
}

/**
 * Props for the EnduserSearchCombobox component
 */
export interface EnduserSearchComboboxProps {
  // ===== Selection (Controlled State) =====
  /** Current selected enduser(s) - MUST be controlled externally */
  value: Enduser[];

  /** Callback when selection changes */
  onChange: (value: Enduser[]) => void;

  /** Enable multi-select mode */
  multiple?: boolean;

  // ===== Search Configuration =====
  /** Debounce delay for API search in milliseconds (default: 500) */
  debounceMs?: number;

  // ===== Query Configuration =====
  /**
   * Optional additional TanStack Query options.
   * The `model`, `method`, `request.search`, and `request.limit` are managed internally.
   * You can provide additional request params (e.g., filters), or override `enabled`, `staleTime`, etc.
   */
  queryOptions?: Partial<
    Omit<
      UseTellescopeInfiniteQueryOptions<"endusers", "getSome", string | undefined, Error>,
      "model" | "method" | "getNextPageParam" | "initialPageParam" | "paginateRequest" | "select"
    >
  > & {
    /** Additional request parameters to merge with the search query */
    requestOverrides?: Partial<ParamsForMethod<"endusers", "getSome">>;
  };

  // ===== UI Customization =====
  /**
   * Default option renderer variant to use.
   * - "default": Shows only enduser name
   * - "phone": Shows enduser name with phone number
   * - "email": Shows enduser name with email
   * Can be overridden by providing formatOption prop.
   */
  defaultRendererVariant?: DefaultRendererVariant;

  /** Custom option renderer - receives enduser data via metadata */
  formatOption?: (props: ComboboxOptionRenderProps<Enduser>) => ReactNode;

  /** Custom selected value formatter */
  formatSelectedValue?: (value: Enduser[]) => ReactNode;

  /** Placeholder text for trigger button (default: "Select enduser...") */
  placeholder?: string;

  /** Placeholder text for search input (default: "Search by name, email, or phone...") */
  searchPlaceholder?: string;

  /** Text shown while loading results (default: "Searching...") */
  loadingText?: string;

  /** Text shown when no results found (default: "No endusers found") */
  emptyText?: string;

  /** Show clear button (default: true) */
  clearable?: boolean;

  /** CSS class for trigger button */
  className?: string;

  /** CSS class for popover content */
  popoverClassName?: string;

  /** Accessibility label */
  "aria-label"?: string;

  /** Custom filter function for client-side filtering of options */
  customFilter?: (searchTerm: string, options: ComboboxOption<Enduser>[]) => ComboboxOption<Enduser>[];

  /** When true, display only "{count} selected" instead of listing names */
  onlyDisplayCount?: boolean;

  /** When true, display selected endusers as removable badges */
  showAsBadges?: boolean;
}

/**
 * Reusable enduser search combobox component with debounced API search.
 *
 * This component wraps the base Combobox with enduser-specific functionality:
 * - Automatic TanStack Query integration with debounced search
 * - Controlled enduser selection (single or multi-select)
 * - Three default option renderer variants (default, phone, email)
 * - Customizable query options and rendering
 *
 * @example
 * // Simple single-select
 * const [selectedEnduser, setSelectedEnduser] = useState<Enduser>();
 * <EnduserSearchCombobox
 *   value={selectedEnduser}
 *   onChange={setSelectedEnduser}
 * />
 *
 * @example
 * // Multi-select with phone display
 * const [selectedEndusers, setSelectedEndusers] = useState<Enduser[]>([]);
 * <EnduserSearchCombobox
 *   multiple
 *   value={selectedEndusers}
 *   onChange={setSelectedEndusers}
 *   defaultRendererVariant="phone"
 * />
 *
 * @example
 * // With filters and custom options
 * <EnduserSearchCombobox
 *   value={selectedEnduser}
 *   onChange={setSelectedEnduser}
 *   queryOptions={{
 *     requestOverrides: {
 *       filter: { tags: ["vip"] }
 *     },
 *     staleTime: 30000
 *   }}
 * />
 *
 * @example
 * // Custom rendering
 * <EnduserSearchCombobox
 *   value={selectedEnduser}
 *   onChange={setSelectedEnduser}
 *   formatOption={(props) => <CustomEnduserItem {...props} />}
 * />
 */
export function EnduserSearchCombobox({
  value,
  onChange,
  multiple,
  debounceMs = 500,
  queryOptions,
  defaultRendererVariant = "default",
  formatOption,
  formatSelectedValue,
  placeholder = i18n.t("Select enduser...", { ns: "tickets" }),
  searchPlaceholder = i18n.t("Search by name, email, or phone...", { ns: "tickets" }),
  loadingText = i18n.t("Searching...", { ns: "tickets" }),
  emptyText = i18n.t("No endusers found", { ns: "tickets" }),
  clearable = true,
  className,
  popoverClassName,
  "aria-label": ariaLabel,
  customFilter,
  onlyDisplayCount,
  showAsBadges,
}: EnduserSearchComboboxProps) {
  // Internal search state with debouncing
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery] = useDebouncedValue(searchQuery, debounceMs);

  // Query endusers with debounced search
  const endusersQuery = useTellescopeInfiniteQuery({
    model: "endusers",
    method: "getSome",
    request: {
      limit: 20,
      search: debouncedSearchQuery ? { query: debouncedSearchQuery } : undefined,
      ...queryOptions?.requestOverrides,
    },
    select: (data) => data.pages.flat(),
    ...queryOptions,
  });

  // Map endusers to combobox options
  const comboboxOptions: ComboboxOption<Enduser>[] = useMemo(
    () =>
      endusersQuery.data?.map((enduser) => ({
        value: enduser.id,
        label: formatName(enduser),
        metadata: { enduser },
      })) ?? [],
    [endusersQuery.data]
  );

  // Convert external value to combobox format (always array for internal Combobox)
  const comboboxValue: ComboboxOption<Enduser>[] = useMemo(() => {
    const arrayValue = value;
    return arrayValue.map((enduser) => ({
      value: enduser.id,
      label: formatName(enduser),
      metadata: { enduser },
    }));
  }, [value]);

  // Handle selection changes - convert array back to single value if needed
  const handleChange = useCallback(
    (options: ComboboxOption<Enduser>[]) => {
      const endusers = options.map((opt) => opt.metadata?.enduser).filter((e): e is Enduser => !!e);

      if (multiple) {
        onChange(endusers);
        return;
      }

      const currentIds = value.map(({ id }) => id);
      const newVal = endusers.filter(({ id }) => !currentIds.includes(id));
      onChange(newVal.slice(0, 1));
    },
    [multiple, onChange, value]
  );

  // Default option renderer
  const defaultFormatOption = useMemo(
    () => (props: ComboboxOptionRenderProps<Enduser>) => {
      const enduser = props.option.metadata?.enduser;
      if (!enduser) return null;
      return <DefaultEnduserOption enduser={enduser} isSelected={props.isSelected} variant={defaultRendererVariant} />;
    },
    [defaultRendererVariant]
  );

  // Wrap custom formatSelectedValue to convert ComboboxOption to Enduser
  const wrappedFormatSelectedValue = useMemo(() => {
    // Custom formatter takes priority
    if (formatSelectedValue) {
      return (val: ComboboxOption<Enduser>[]) => {
        const endusers = val.map((opt) => opt.metadata?.enduser).filter((e): e is Enduser => !!e);
        return formatSelectedValue(endusers);
      };
    }

    if (onlyDisplayCount) {
      return (val: ComboboxOption<Enduser>[]) => {
        if (val.length === 0) return null;
        return <span>{i18n.t("{{amount}} selected", { amount: val.length, ns: "tickets" })}</span>;
      };
    }

    return undefined;
  }, [formatSelectedValue, onlyDisplayCount]);

  return (
    <div className="space-y-2">
      <Combobox
        // Selection
        value={comboboxValue}
        onChange={handleChange}
        multiple
        // Options
        options={comboboxOptions}
        alwaysFilter={false}
        customFilter={customFilter}
        // Search
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        searchPlaceholder={searchPlaceholder}
        // Rendering
        formatOption={formatOption ?? defaultFormatOption}
        formatSelectedValue={wrappedFormatSelectedValue}
        // Loading states
        isLoading={endusersQuery.isLoading || searchQuery !== debouncedSearchQuery}
        loadingText={loadingText}
        emptyText={emptyText}
        // UI
        placeholder={placeholder}
        clearable={clearable}
        className={className}
        popoverClassName={popoverClassName}
        aria-label={ariaLabel}
      />
      {showAsBadges && value.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {value.map((enduser) => (
            <DeletableBadge key={enduser.id} onDelete={() => onChange(value.filter((e) => e.id !== enduser.id))}>
              {formatName(enduser)}
            </DeletableBadge>
          ))}
        </div>
      )}
    </div>
  );
}
